import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IcPageHeader, IcButton, IcEmptyState, IcAlert } from "@ukic/react";

import { api, type Device } from "../api";
import { timeAgo } from "../format";
import { DataTable, type Column } from "../components/DataTable";
import { Loading } from "../components/Loading";
import { ErrorState } from "../components/ErrorState";
import { useRole } from "../useRole";
import { useToast } from "../toast";

export default function Pending() {
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const toast = useToast();

  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [approving, setApproving] = useState<Record<string, boolean>>({});

  const load = useCallback(() => {
    setError(null);
    api
      .listDevices()
      .then(setDevices)
      .catch(setError);
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const approve = useCallback(
    async (d: Device) => {
      setApproving((m) => ({ ...m, [d.id]: true }));
      try {
        await api.approveDevice(d.id);
        toast.success("Device approved", `${d.name} is provisioning a tunnel.`);
        load();
      } catch (e) {
        toast.error("Approval failed", e instanceof Error ? e.message : String(e));
      } finally {
        setApproving((m) => ({ ...m, [d.id]: false }));
      }
    },
    [toast, load],
  );

  if (!isAdmin) {
    return (
      <div className="hl-page">
        <IcAlert
          variant="info"
          heading="Admins only"
          message="You need the admin role to review pending devices."
        />
      </div>
    );
  }

  const columns: Column<Device>[] = [
    { key: "name", header: "Name", cell: (d) => <strong>{d.name}</strong> },
    {
      key: "location",
      header: "Location",
      cell: (d) => d.location || <span className="hl-muted">—</span>,
    },
    { key: "sdr", header: "SDR", cell: (d) => d.sdr_type ?? "unknown" },
    { key: "id", header: "Device ID", cell: (d) => <code>{d.id}</code> },
    { key: "seen", header: "First seen", cell: (d) => timeAgo(d.last_seen) },
    {
      key: "action",
      header: "",
      align: "right",
      cell: (d) => (
        <IcButton
          variant="primary"
          size="small"
          loading={!!approving[d.id]}
          onClick={(e) => {
            // Don't trigger row navigation.
            (e as unknown as Event).stopPropagation?.();
            approve(d);
          }}
        >
          Approve
        </IcButton>
      ),
    },
  ];

  const pending = (devices ?? []).filter((d) => d.status === "pending");

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader heading="Pending devices" subheading="Awaiting approval">
        <IcButton slot="actions" variant="secondary" onClick={load}>
          Refresh
        </IcButton>
      </IcPageHeader>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : devices === null ? (
        <Loading label="Loading pending devices…" />
      ) : pending.length === 0 ? (
        <IcEmptyState
          heading="Nothing pending"
          subheading="Newly enrolled devices will appear here for approval."
        />
      ) : (
        <DataTable
          caption="Pending devices"
          columns={columns}
          rows={pending}
          rowKey={(d) => d.id}
          onRowClick={(d) => navigate(`/devices/${encodeURIComponent(d.id)}`)}
        />
      )}
    </div>
  );
}
